import {
  Check,
  CheckSquareOffset,
  Circle,
  DotsThree,
  FileCsv,
  FilePdf,
  Printer,
  Scroll,
  Trash,
} from '@phosphor-icons/react';
import {
  Button,
  Dropdown,
  Flex,
  Space,
  Spin,
  Tag,
  Typography,
  theme as antTheme,
} from 'antd';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { HeaderBackButton } from '../../components';
import { COLOR } from '../../styles/theme';
import { BillingService, CommonUtility, ToastMessage } from '../../utility';
import { invoiceStatusText } from './common';

const { useToken } = antTheme;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

const darkButtonStyle = {
  background: COLOR.black,
  borderColor: COLOR.black,
  color: COLOR.white,
};

function getStatusLabel(status) {
  return invoiceStatusText[status] || CommonUtility.toTitleCase(status || '');
}

function getStatusTagColor(status) {
  if (status === 'open') return 'success';
  if (status === 'pre-approval') return 'warning';
  return 'default';
}

/** AppHeader left: back, title, status. */
export function InvoiceDetailHeaderLead({ title, status, loading }) {
  const { token } = useToken();
  const statusLabel = getStatusLabel(status);

  return (
    <Flex
      align="center"
      gap={12}
      wrap="nowrap"
      style={{ minWidth: 0, maxWidth: '100%', height: '100%' }}
    >
      <HeaderBackButton />
      {loading ? (
        <Spin size="small" />
      ) : title || statusLabel ? (
        <Flex align="center" gap={8} style={{ minWidth: 0 }} wrap="nowrap">
          {title ? (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <IconWrap>
                <Scroll size={18} color={token.colorText} />
              </IconWrap>
              <Typography.Text
                strong
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  maxWidth: 280,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography.Text>
            </Flex>
          ) : null}
          {statusLabel ? (
            <Tag
              bordered={false}
              color={getStatusTagColor(status)}
              style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Circle size={8} weight="fill" />
              {statusLabel}
            </Tag>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

function InvoicePdfActions({ invoiceData }) {
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await BillingService.getInvoicePDF(
        invoiceData._id,
        { responseType: 'blob' },
        { 'Content-Type': 'application/pdf' },
      );
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceData.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDownloading(false);
    }
  };

  const printPDF = async () => {
    try {
      setPrinting(true);
      const response = await BillingService.getInvoicePDF(
        invoiceData._id,
        { responseType: 'blob' },
        { 'Content-Type': 'application/pdf' },
      );
      const url = window.URL.createObjectURL(response);
      window.open(url);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setPrinting(false);
    }
  };

  if (!invoiceData?._id) return null;

  return (
    <>
      <Button
        icon={<FilePdf size={16} />}
        loading={downloading}
        disabled={printing}
        onClick={downloadPDF}
      >
        PDF
      </Button>
      {invoiceData?.status === 'open' ? (
        <Button
          icon={<Printer size={16} />}
          loading={printing}
          disabled={downloading}
          onClick={printPDF}
        >
          Print
        </Button>
      ) : null}
    </>
  );
}

/** Secondary header: PDF, primary CTA, overflow menu (antd). */
export function InvoiceDetailSecondaryHeader({
  invoiceData,
  mode = 'detail',
  step = 0,
  hasDirtyFields = false,
  markLoading = false,
  preApprovalLoading = false,
  csvExportLoading = false,
  archiveDisabled = false,
  onSendInvoice,
  onApproveClick,
  onMarkPreApproved,
  onExportCSV,
  onArchive,
}) {
  const allowedStatus = ['pre-approval', 'draft'];
  const showApproveAndSend =
    mode === 'detail' && allowedStatus.includes(invoiceData?.status);

  const menuItems = useMemo(() => {
    const items = [];

    if (mode === 'create' && step !== 0) {
      items.push({
        key: 'pre-approve',
        label: 'Pre Approve',
        icon: <Check size={16} />,
        disabled: markLoading || preApprovalLoading,
        onClick: onMarkPreApproved,
      });
    }

    if (mode === 'detail' || (mode === 'create' && step !== 0)) {
      items.push({
        key: 'csv',
        label: 'CSV Export',
        icon: <FileCsv size={16} />,
        disabled: csvExportLoading,
        onClick: onExportCSV,
      });
    }

    items.push({
      key: 'archive',
      label: 'Archive',
      icon: <Trash size={16} />,
      danger: true,
      disabled: archiveDisabled,
      onClick: onArchive,
    });

    return items;
  }, [
    mode,
    step,
    markLoading,
    preApprovalLoading,
    csvExportLoading,
    archiveDisabled,
    onMarkPreApproved,
    onExportCSV,
    onArchive,
  ]);

  return (
    <Flex align="center" style={{ width: '100%' }}>
      <Space size="middle" style={{ marginLeft: 'auto' }}>
        <InvoicePdfActions invoiceData={invoiceData} />

        {mode === 'create' && step === 0 ? (
          <Button
            type="primary"
            style={darkButtonStyle}
            loading={markLoading}
            disabled={hasDirtyFields || preApprovalLoading}
            onClick={onSendInvoice}
          >
            Mark for Approval
          </Button>
        ) : null}

        {mode === 'create' && step !== 0 ? (
          <Button
            type="primary"
            style={darkButtonStyle}
            icon={<CheckSquareOffset size={16} />}
            loading={markLoading}
            disabled={preApprovalLoading}
            onClick={onApproveClick}
          >
            Approve & Send
          </Button>
        ) : null}

        {showApproveAndSend ? (
          <Button
            type="primary"
            style={darkButtonStyle}
            icon={<CheckSquareOffset size={16} />}
            loading={markLoading}
            onClick={onApproveClick}
          >
            Approve & Send
          </Button>
        ) : null}

        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="primary"
            style={darkButtonStyle}
            icon={<DotsThree size={18} weight="bold" />}
            aria-label="More actions"
          />
        </Dropdown>
      </Space>
    </Flex>
  );
}
